import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import TextInput from "../../components/TextInput";
import Dropdown from "../../components/Dropdown";
import Button from "../../components/Button";
import { styled } from "@mui/material/styles";
import {
  Mail,
  MapPin,
  BookOpen,
  TrendingUp,
  MessageCircle,
  Clock,
} from "lucide-react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { colors } from "../../config/colors";
import {
  getContactDetails as apiGetContactDetails,
  submitContactMessage,
} from "../../api";
import { contactSubjectOptions } from "../../config/staticData";

// Unique: Card with tilt-on-hover and gradient border
const ContactCard = styled(Paper)(({ theme }) => ({
  borderRadius: "20px",
  padding: theme.spacing(3),
  height: "100%",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  border: `2px solid ${colors.gray[200]}`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${colors.primary[500]}, ${colors.secondary[500]})`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover": {
    transform: "translateY(-8px) scale(1.02)",
    boxShadow: `0 24px 48px -12px ${colors.primary[500]}35`,
    borderColor: colors.primary[300],
    "&::before": { opacity: 1 },
  },
}));

// Form container with soft gradient
const FormCard = styled(Paper)(({ theme }) => ({
  borderRadius: "24px",
  padding: theme.spacing(4),
  background: `linear-gradient(145deg, ${colors.primary[50]}15, white)`,
  border: `2px solid ${colors.primary[100]}`,
  boxShadow: `0 4px 24px ${colors.primary[500]}08`,
}));

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [contactDetails, setContactDetails] = useState({
    facebook_link: "",
    twitter_link: "",
    instagram: "",
    address: "",
    all_course_no1: "",
    all_course_no2: "",
    trading_no: "",
    email: "",
    whatsapp: "",
  });
  const [error, setError] = useState("");

  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [formSuccess, setFormSuccess] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        if (!value.trim()) error = "Name is required";
        else if (value.trim().length < 2)
          error = "Name must be at least 2 characters";
        break;
      case "email":
        if (!value.trim()) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
          error = "Please enter a valid email address";
        break;
      case "phone":
        if (!value.trim()) error = "Phone number is required";
        else if (!/^[0-9]{10}$/.test(value.trim()))
          error = "Please enter a valid 10-digit phone number";
        break;
      case "subject":
        if (!value) error = "Subject is required";
        break;
      case "message":
        if (!value.trim()) error = "Message is required";
        else if (value.trim().length < 10)
          error = "Message must be at least 10 characters";
        break;
      default:
        break;
    }
    return error;
  };

  const isFormValid = useMemo(() => {
    const requiredFields = ["name", "email", "phone", "subject", "message"];
    const hasEmptyFields = requiredFields.some(
      (field) => !formData[field].trim(),
    );
    const hasErrors = Object.values(fieldErrors).some((err) => err !== "");
    return !hasEmptyFields && !hasErrors;
  }, [formData, fieldErrors]);

  useEffect(() => {
    let mounted = true;

    const fetchContactDetails = async () => {
      try {
        const data = await apiGetContactDetails();
        if (!mounted || !data) return;
        setContactDetails({
          facebook_link: data.facebook_link || "",
          twitter_link: data.twitter_link || "",
          instagram: data.instagram || "",
          address: data.address || "",
          all_course_no1: data.all_course_no1 || "",
          all_course_no2: data.all_course_no2 || "",
          trading_no: data.trading_no || "",
          email: data.email || "",
          whatsapp: data.whatsapp || "",
        });
        setError("");
      } catch (err) {
        if (!mounted) return;
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load contact details",
        );
      }
    };

    fetchContactDetails();

    return () => {
      mounted = false;
    };
  }, []);

  const contactChannels = useMemo(
    () => [
      {
        title: "All Courses",
        subtitle: "Enrollment & course queries",
        icon: BookOpen,
        href: `tel:${contactDetails.all_course_no1}`,
        primary: contactDetails.all_course_no1,
        secondary: contactDetails.all_course_no2,
        color: colors.primary[500],
      },
      {
        title: "Trading Support",
        subtitle: "Trading & broker assistance",
        icon: TrendingUp,
        href: `tel:${contactDetails.trading_no}`,
        primary: contactDetails.trading_no,
        secondary: null,
        color: colors.secondary[500],
      },
      {
        title: "Email Us",
        subtitle: "General enquiries",
        icon: Mail,
        href: `mailto:${contactDetails.email}`,
        primary: contactDetails.email,
        secondary: null,
        color: colors.primary[600],
      },
      {
        title: "Visit Us",
        subtitle: "Nadiad, Gujarat",
        icon: MapPin,
        href:
          "https://maps.google.com/?q=" +
          encodeURIComponent(contactDetails.address),
        primary: contactDetails.address,
        secondary: null,
        color: colors.secondary[600],
      },
    ],
    [contactDetails],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    const error = validateField(name, value);
    setFieldErrors({ ...fieldErrors, [name]: error });
    setFormSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setFormSubmitting(true);
    setFormSuccess("");
    try {
      await submitContactMessage(formData);
      setFormSuccess(
        "Thank you! Your message has been sent. We will get back to you shortly.",
      );
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to send your message. Please try again.";
      setFieldErrors({ ...fieldErrors, global: msg });
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8faf9]">
      <Header />

      <main className="flex-grow">
        {/* Hero - compact, with badge */}
        <Box
          sx={{
            py: 8,
            px: 2,
            background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 50%, ${colors.secondary[600]} 100%)`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 40%)",
              pointerEvents: "none",
            }}
          />
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 3,
              }}
            >
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    letterSpacing: 3,
                    fontWeight: "bold",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Contact
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ color: "white", fontWeight: "bold", mb: 1 }}
                >
                  Get in touch
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "1.1rem",
                    maxWidth: 480,
                  }}
                >
                  Have questions about our courses, trading support, or broker
                  comparisons? We&apos;re here to help.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "rgba(255,255,255,0.15)",
                  px: 3,
                  py: 2,
                  borderRadius: "16px",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <Clock size={22} color="white" />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)", display: "block" }}
                  >
                    Response time
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1rem",
                    }}
                  >
                    Within 24 hours
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Contact cards + Form */}
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1.2fr" },
              gap: 6,
              alignItems: "start",
            }}
          >
            {/* Left: Contact channel cards */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: colors.primary[800], mb: 1 }}
              >
                Reach us directly
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose the channel that works best for you.
              </Typography>
              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {error}
                </Typography>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {contactChannels.map((channel) => (
                  <a
                    key={channel.title}
                    href={channel.href}
                    target={
                      channel.href.startsWith("http") ? "_blank" : undefined
                    }
                    rel={
                      channel.href.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                    style={{ textDecoration: "none" }}
                  >
                    <ContactCard elevation={0}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "14px",
                          background: channel.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <channel.icon size={24} color="white" />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          color: colors.primary[800],
                          mb: 0.5,
                        }}
                      >
                        {channel.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        {channel.subtitle}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: colors.primary[700] }}
                      >
                        {channel.primary}
                      </Typography>
                      {channel.secondary && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {channel.secondary}
                        </Typography>
                      )}
                    </ContactCard>
                  </a>
                ))}
              </Box>

              {/* WhatsApp CTA */}
              <a
                href="https://chat.whatsapp.com/D5ZQJZxkDk9H4jgcWNMWAG"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, #25D366, #128C7E)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 28px rgba(37, 211, 102, 0.4)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      bgcolor: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MessageCircle size={28} color="white" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Chat on WhatsApp
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.95 }}>
                      Quick response for urgent queries
                    </Typography>
                  </Box>
                </Paper>
              </a>
            </Box>

            {/* Right: Contact form */}
            <FormCard elevation={0}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: colors.primary[800], mb: 1 }}
              >
                Send us a message
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Fill in the form below and we&apos;ll get back to you soon.
              </Typography>

              {formSuccess && (
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{ mb: 2, fontWeight: 500 }}
                >
                  {formSuccess}
                </Typography>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <TextInput
                    name="name"
                    label="Full Name"
                    placeholder="Enter your full name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    error={fieldErrors.name}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextInput
                      name="email"
                      label="Email"
                      type="email"
                      placeholder="Enter your email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      error={fieldErrors.email}
                    />
                    <TextInput
                      name="phone"
                      label="Phone"
                      placeholder="Enter 10 digit number"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      error={fieldErrors.phone}
                    />
                  </Box>
                  <Dropdown
                    name="subject"
                    label="Subject"
                    placeholder="Select a subject"
                    required
                    options={contactSubjectOptions}
                    value={formData.subject}
                    onChange={handleChange}
                    error={fieldErrors.subject}
                  />

                  <TextInput
                    name="message"
                    label="Message"
                    placeholder="How can we help you?"
                    multiline
                    rows={4}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    error={fieldErrors.message}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={formSubmitting || !isFormValid}
                    className="w-full sm:w-auto"
                  >
                    {formSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </Box>
              </form>
            </FormCard>
          </Box>
        </Container>

        {/* Map / Address strip */}
        <Box
          sx={{
            py: 4,
            px: 2,
            bgcolor: colors.primary[50],
            borderTop: `1px solid ${colors.primary[100]}`,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "16px",
                    background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[500]})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MapPin size={28} color="white" />
                </Box>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: colors.primary[800] }}
                  >
                    {contactDetails.address || "Loading address..."}
                  </Typography>
                </Box>
              </Box>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(contactDetails.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: colors.primary[500],
                    color: colors.primary[600],
                    fontWeight: "bold",
                    borderRadius: "12px",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: colors.primary[600],
                      bgcolor: colors.primary[50],
                    },
                  }}
                >
                  Open in Maps
                </Button>
              </a>
            </Box>
          </Container>
        </Box>
      </main>

      <Footer />
    </div>
  );
};

export default ContactUs;
